import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableTeamSelect } from './SearchableTeamSelect';
import { Label } from '@/components/ui/label';
import { useGetFeedLeaguesQuery, useGetFeedTeamsQuery } from '@/api/feed';
import { resolveTeamImageUrl, NCAAMB_MANUAL_ASSETS, type Sport } from '@/utils/jersey-mapping';

interface TeamSelection {
  teamName: string;
  teamId?: string; // Team externalId
  teamAbbr: string;
  teamLogoUrl: string;
  sport: string;
  teamMarket?: string; // City/market name (e.g., "Washington" for "Washington Wizards")
}

interface TeamSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (selection: TeamSelection) => void;
  selectedTeams?: string[];
  fieldLabel?: string;
  /** When set, auto-selects this league and disables the league selector (e.g., when pickem sport is not "Mixed") */
  restrictedLeagueId?: string;
  /** Contest League IP flag — IP on bakes the team logo, off bakes the team avatar (jersey) */
  leagueIp?: boolean;
}

export function TeamSelectorModal({
  open,
  onOpenChange,
  onSelect,
  selectedTeams = [],
  fieldLabel = 'Option',
  restrictedLeagueId,
  leagueIp = false,
}: TeamSelectorModalProps) {
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [imageError, setImageError] = useState(false);

  // Auto-select restricted league when modal opens
  useEffect(() => {
    if (open && restrictedLeagueId && !selectedLeagueId) {
      setSelectedLeagueId(restrictedLeagueId);
    }
  }, [open, restrictedLeagueId, selectedLeagueId]);

  // Fetch data from feed service
  const { data: leagues = [], isLoading: isLoadingLeagues } = useGetFeedLeaguesQuery(undefined);
  const { data: feedTeams = [], isLoading: isLoadingTeams } =
    useGetFeedTeamsQuery(selectedLeagueId);

  // For NCAAMB, merge manual assets (e.g. Arbys-HOME, Arbys-AWAY) with feed teams
  const teams =
    selectedLeagueId === 'ncaamb'
      ? [...NCAAMB_MANUAL_ASSETS.map(a => ({ ...a, market: undefined })), ...feedTeams]
      : feedTeams;

  const filteredTeams =
    selectedTeams.length === 2
      ? teams.filter(team => selectedTeams.includes(team.externalId))
      : teams;

  // Reset downstream selections when parent changes
  useEffect(() => {
    setSelectedTeamId('');
  }, [selectedLeagueId]);

  // Find selected entities
  const selectedLeague = leagues.find(l => l.externalId === selectedLeagueId);
  const selectedTeam = teams.find(t => t.externalId === selectedTeamId);

  // Generate preview URL when team is selected. The image follows the
  // contest's League IP setting: team logo when on, team avatar (jersey) when
  // off — the resolver falls back to the logo for sports with no avatar.
  useEffect(() => {
    if (selectedTeam && selectedLeague) {
      const sport = selectedLeague.sportId as Sport;
      const url = resolveTeamImageUrl({
        teamAbbr: selectedTeam.abbreviation || '',
        sport,
        leagueIp,
      });
      setPreviewUrl(url);
      setImageError(false);
    } else {
      setPreviewUrl('');
    }
  }, [selectedTeam, selectedLeague, leagueIp]);

  const handleSelect = () => {
    if (selectedTeam && selectedLeague && previewUrl) {
      const displayName = selectedTeam.market
        ? `${selectedTeam.market} ${selectedTeam.name}`
        : selectedTeam.name;
      onSelect({
        teamName: displayName,
        teamId: selectedTeam.externalId,
        teamAbbr: selectedTeam.abbreviation || '',
        teamLogoUrl: previewUrl,
        sport: selectedLeague.sportId,
        teamMarket: selectedTeam.market,
      });
      handleClose();
    }
  };

  const handleClose = () => {
    // Only reset league if not restricted
    if (!restrictedLeagueId) {
      setSelectedLeagueId('');
    }
    setSelectedTeamId('');
    setPreviewUrl('');
    setImageError(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Select Team {fieldLabel && `for ${fieldLabel}`}</DialogTitle>
          <DialogDescription>Choose a league and team from the sports feed</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="league-select">League</Label>
            {restrictedLeagueId && (
              <p className="text-xs text-muted-foreground mb-1">
                Pre-selected based on pickem sport
              </p>
            )}
            <Select
              value={selectedLeagueId}
              onValueChange={setSelectedLeagueId}
              disabled={isLoadingLeagues || !!restrictedLeagueId}
            >
              <SelectTrigger id="league-select">
                <SelectValue
                  placeholder={isLoadingLeagues ? 'Loading leagues...' : 'Select a league'}
                />
              </SelectTrigger>
              <SelectContent>
                {leagues.map(league => (
                  <SelectItem key={league.externalId} value={league.externalId}>
                    {league.name} ({league.sportId})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedLeagueId && (
            <div className="space-y-2">
              <Label htmlFor="team-select">Team</Label>
              <SearchableTeamSelect
                teams={filteredTeams}
                value={selectedTeamId}
                onValueChange={setSelectedTeamId}
                disabled={isLoadingTeams}
                placeholder={isLoadingTeams ? 'Loading teams...' : 'Select a team'}
                id="team-select"
              />
            </div>
          )}

          {previewUrl && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="relative w-full h-64 border rounded-md overflow-hidden bg-gray-50">
                {!imageError ? (
                  <img
                    src={previewUrl}
                    alt="Team logo preview"
                    className="w-full h-full object-contain"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="flex w-full h-full items-center justify-center text-sm text-muted-foreground">
                    Failed to load team logo
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground break-all">{previewUrl}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={!selectedTeamId || !previewUrl || imageError}>
            Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
